import type { ElementNodeDto } from '@xshop/shared';
import { pictureDescription, placeName } from './linkNames';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, linkTarget, textProp, urlProp, type ElementProps, textDirection } from './elementProps';
import { ELEMENT_ICONS } from './elementIcons';
import { ShareButton } from './ShareButton';

// The three things on a page that go somewhere. All three resolve their address through `isSafeUrl`, so
// a `javascript:` a merchant pasted becomes a button that does nothing rather than one that runs.
//
// A button and a link carry their words in a prop, which is why the catalog marks both of them `void`;
// only `block-link` draws children, because a whole card being one link is the whole of what it is for.

/** What a button is for. `submit` is the only one that may reach a form the element sits inside; `share` shares the page. */
const BUTTON_KINDS = ['button', 'submit', 'share'] as const;

/** LightFunnels' named primary actions, exactly as `buttonElement` offers them; `none` is "No action selected". */
const PRIMARY_ACTIONS = ['none', 'process-payment', 'collect-lead', 'validate-coupon', 'publish-review'] as const;

// Answers what a press does: every named primary action posts the form around it, so naming one submits.
const pressKind = (props: ElementProps): (typeof BUTTON_KINDS)[number] =>
  choiceProp(props, 'primaryAction', PRIMARY_ACTIONS, 'none') === 'none'
    ? choiceProp(props, 'kind', BUTTON_KINDS, 'button')
    : 'submit';

/** Which side of the words the glyph sits on, or neither, exactly as `buttonElement` offers it. */
const ICON_SIDES = ['none', 'before', 'after'] as const;

/** The size a button's glyph is drawn at: the words' own, since it is read as one of them. */
const BUTTON_ICON = 18;

// Draws the glyph a merchant put on one side of a button, or nothing when they named none for that side.
const buttonIcon = (props: ElementProps, side: (typeof ICON_SIDES)[number]) => {
  if (choiceProp(props, 'iconPosition', ICON_SIDES, 'none') !== side) return null;
  const Glyph = ELEMENT_ICONS[textProp(props, 'icon')];
  return Glyph ? <Glyph size={BUTTON_ICON} color="currentColor" aria-hidden="true" /> : null;
};

/**
 * Draws the thing a merchant clicks. It is an anchor when it has somewhere to go and a button when it
 * does not, because a button that navigates is one a shopper cannot open in a new tab and a screen
 * reader announces wrongly.
 */
export const ButtonElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const text = textProp(node.props, 'label') || textProp(node.props, 'text');
  const url = urlProp(node.props, 'href');
  // An icon-only button keeps its words for a screen reader, out of sight, so it never loses its name.
  const shown = boolProp(node.props, 'hideLabel') ? 'xe-sr-only' : undefined;
  const words = <>{buttonIcon(node.props, 'before')}<span dir="auto" className={shown}>{text}</span>{buttonIcon(node.props, 'after')}</>;
  if (url) return <a {...box} href={url} {...linkTarget(node.props)}>{words}</a>;
  const kind = pressKind(node.props);
  if (kind === 'share') return <ShareButton box={box} words={words} />;
  return <button {...box} type={kind}>{words}</button>;
};

// Draws a link: the merchant's words, pointing at the address they gave, or plain text when they gave none.
export const LinkElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const text = textProp(node.props, 'text') || textProp(node.props, 'label');
  const url = urlProp(node.props, 'href');
  // A link's words take their own direction, so a title written in Arabic and French reads in its own order.
  const dir = textDirection(node.props);
  if (!url) return <span {...box} dir={dir}>{text}</span>;
  return <a {...box} href={url} dir={dir} {...linkTarget(node.props)}>{text}</a>;
};

// Reports whether a subtree shows any words of its own, which then name the link it sits in.
function hasWords(node: ElementNodeDto): boolean {
  if (['text', 'label', 'title'].some((key) => typeof node.props[key] === 'string' && (node.props[key] as string).trim() !== '')) return true;
  // Words bound to the store's data — a collection's title, a product's name — are words the shopper sees too.
  if (['text', 'label', 'title'].some((key) => Boolean(node.bindings?.[key]))) return true;
  return node.children.some(hasWords);
}

/**
 * Draws a whole subtree that is one link — a card a shopper clicks anywhere on. Its children still
 * draw, which is the difference between this and a link: it is the box that navigates, not the words.
 */
export const BlockLinkElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { store } = useElementRender();
  const href = urlProp(node.props, 'href');
  if (!href) return <div {...box}>{children}</div>;
  // Its label names it only when the card has no words of its own: a name that leaves out the words a shopper sees is
  // one a voice-control user cannot say (Lighthouse's label-content-name-mismatch). A card of pictures alone is named
  // by its label, else its picture's description, else where it goes.
  const name = node.children.some(hasWords) ? undefined
    : textProp(node.props, 'label') || pictureDescription(node) || placeName(href, store?.language ?? 'en');
  return (
    <a {...box} href={href} {...linkTarget(node.props)} aria-label={name}>
      {children}
    </a>
  );
};
